import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Cog, Boxes, ShieldCheck, Recycle, FileUp, Ruler, Layers, Gauge, CheckCircle2, Download } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];
const NAVY = "#0A192F";

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div className={className} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.7, ease, delay }}>{children}</motion.div>
  );
}

const PRODUCT_CARDS = [
  {
    icon: Cog, title: "Custom Gear Manufacturing",
    img: "https://images.pexels.com/photos/28752154/pexels-photo-28752154.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=700&w=1000",
    pdf: "/catalogues/RG_CP_0321.pdf", pdfLabel: "Download Spec Sheet (PDF)",
    items: [
      "Spiral & Straight Bevel Gears — Gleason/Klingenberg profiles up to 8 module on generators, >12 up to 20 module on SPM",
      "Spur & Helical Gears — Ø30mm to Ø1.5m (1–16 module), wear-resistant & low vibration",
      "Worm Wheels & Worm Shafts — standard and custom ratios, low-maintenance profiles",
    ],
  },
  {
    icon: Boxes, title: "Industrial Gearboxes",
    img: "https://images.unsplash.com/photo-1736161999319-808bb8e66300?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    pdf: "/catalogues/RG_CP_0126.pdf", pdfLabel: "Download Catalogue (PDF)",
    items: [
      "Custom & heavy-duty units — tailor-made, SRC gearboxes, four roller crusher assemblies",
      "Standard reduction — single (CU type) & double reduction worm gearboxes",
      "Inline helical gearbox",
    ],
  },
  {
    icon: ShieldCheck, title: "Defense & Strategic Engineering",
    img: "/defense/m2-bradley-sprocket.png",
    href: "/defense", linkLabel: "View Defense Gallery",
    items: [
      "Aerospace gear mechanisms & M2 Bradley sprockets",
      "Navy bevel gearboxes, gun mounts & planetary gearboxes",
      "Armored vehicle spares to strategic specifications",
    ],
  },
  {
    icon: Recycle, title: "Reverse Engineering & Heavy-Industry Spares",
    img: "/defense/gear-mechanism.jpg",
    pdf: "/catalogues/RG_CP_0321.pdf", pdfLabel: "Download Capabilities (PDF)",
    items: [
      "Specialized indigenization for steel & sinter plants",
      "Wind cut-off plates & crusher assemblies",
      "Haulage chain gearboxes and build-to-print spares",
    ],
  },
];

export function ProductsCapabilities() {
  return (
    <section id="products" className="bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="text-center max-w-2xl mx-auto mb-16">
          <span className="font-head font-bold tracked uppercase text-xl lg:text-2xl gold-text">Products & Capabilities</span>
          <h2 className="font-head font-extrabold tracked uppercase text-3xl lg:text-4xl text-[#0A192F] mt-4 leading-tight">
            Engineered For Every Application
          </h2>
          <p className="text-slate-500 mt-5 leading-relaxed">From micron-tolerance gears to two-metre gearboxes and mission-critical defense components.</p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-7">
          {PRODUCT_CARDS.map((c, i) => (
            <Reveal key={i} delay={(i % 2) * 0.08}>
              <div data-testid={`product-card-${i}`} className="card-lift group bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden h-full">
                <div className="relative h-52 overflow-hidden">
                  <img src={c.img} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg gold-gradient flex items-center justify-center"><c.icon size={20} color={NAVY} /></div>
                    <h3 className="font-head font-bold text-white text-lg uppercase tracked-sm">{c.title}</h3>
                  </div>
                </div>
                <div className="p-7 flex flex-col h-full">
                  <ul className="space-y-3 flex-1">
                    {c.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-3 text-slate-600 text-sm leading-relaxed">
                        <CheckCircle2 size={16} className="text-[#D4AF37] mt-0.5 shrink-0" /> {it}
                      </li>
                    ))}
                  </ul>
                  {c.pdf && (
                    <a href={c.pdf} download data-testid={`spec-pdf-${i}`}
                      className="mt-6 inline-flex items-center gap-2 font-head font-bold text-[12px] tracked-sm uppercase text-[#0A192F] hover:text-[#D4AF37] transition-colors">
                      <Download size={16} className="text-[#D4AF37]" /> {c.pdfLabel}
                    </a>
                  )}
                  {c.href && (
                    <Link to={c.href} data-testid={`product-link-${i}`}
                      className="mt-6 inline-flex items-center gap-2 font-head font-bold text-[12px] tracked-sm uppercase text-[#0A192F] hover:text-[#D4AF37] transition-colors">
                      {c.linkLabel} <ArrowRight size={16} className="text-[#D4AF37]" />
                    </Link>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const MACHINES = [
  { icon: Gauge, k: "HAAS VMCs", v: "VF2 · VF5 · VF9 up to 2200 × 1600 × 1000mm" },
  { icon: Layers, k: "PAMA Horizontal Boring", v: "Heavy component machining" },
  { icon: Cog, k: "TOS Gear Hobbing", v: "Up to 16 module (Ø2m)" },
  { icon: Ruler, k: "CMM — Accurate", v: "X-1000 · Y-600 · Z-500 + Mitutoyo instruments" },
];

export function ManufacturingQuality() {
  return (
    <section id="quality" className="bg-[#F8FAFC] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="text-center max-w-2xl mx-auto mb-16">
          <span className="font-head font-bold tracked uppercase text-xl lg:text-2xl gold-text">Manufacturing & Quality Assurance</span>
          <h2 className="font-head font-extrabold tracked uppercase text-3xl lg:text-4xl text-[#0A192F] mt-4 leading-tight">
            Precision, Verified.
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-10 items-stretch">
          <Reveal>
            <div className="rounded-xl overflow-hidden shadow-card-lg h-full min-h-[320px]">
              <img src="https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Modern factory floor" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-white rounded-xl shadow-card border border-slate-100 p-8 h-full">
              <h3 className="font-head font-bold text-[#0A192F] uppercase tracked-sm text-lg">Facility & Machine Highlights</h3>
              <p className="text-slate-500 text-sm mt-2">Modern setup covering turning, milling, hobbing, gear shaping and cylindrical grinding.</p>
              <div className="mt-6 space-y-4">
                {MACHINES.map((m, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-9 h-9 rounded-lg bg-[#0A192F] flex items-center justify-center shrink-0"><m.icon size={17} color="#D4AF37" /></div>
                    <div>
                      <div className="font-head font-bold text-[#0A192F] text-sm uppercase tracked-sm">{m.k}</div>
                      <div className="text-slate-500 text-sm">{m.v}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-7 mt-10">
          <Reveal>
            <div className="bg-white rounded-xl shadow-card border border-slate-100 p-7 h-full">
              <h4 className="font-head font-bold text-[#0A192F] uppercase tracked-sm text-sm mb-3">Material Grades</h4>
              <p className="text-slate-500 text-sm leading-relaxed">Alloy steels, stainless steel, cast iron and custom metallurgical specifications — with heat treatments and defined tooth profiles.</p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="bg-white rounded-xl shadow-card border border-slate-100 p-7 h-full">
              <h4 className="font-head font-bold text-[#0A192F] uppercase tracked-sm text-sm mb-3">Quality Inspection</h4>
              <p className="text-slate-500 text-sm leading-relaxed">Dedicated CMM, Mitutoyo precision instruments and dynamic balancing. Validated to ISO 9001, DIN, AGMA, ASTM, AS, IATF & CSA.</p>
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="bg-[#0A192F] rounded-xl shadow-card-lg p-7 h-full flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded-lg gold-gradient flex items-center justify-center mb-4"><FileUp size={20} color={NAVY} /></div>
                <h4 className="font-head font-bold text-white uppercase tracked-sm text-sm mb-2">CAD / Technical Drawings</h4>
                <p className="text-slate-300 text-sm leading-relaxed">Send us your STEP / DWG / PDF drawings for immediate quote assessment.</p>
              </div>
              <button data-testid="quality-quote-btn" onClick={() => document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-gold mt-6 inline-flex items-center justify-center gap-2 font-head font-bold text-xs tracked-sm uppercase px-6 py-3 rounded-lg">
                Upload For Quote <ArrowRight size={16} />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
